import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Card, Col, Form, Row, Spinner, Tab, Tabs } from 'react-bootstrap'
import useAxiosPrivate from '../../../../../hooks/useAxiosPrivate'
import { useToast } from '../../../../common/toast/hooks/useToast'
import { useAppSelector } from '../../../../../store/hooks'
import { Language } from '../../../../../features/security/security.slice'
import HtmlEditor from '../../../../common/forms/fields/HtmlEditor'
import { Newsletter } from './Newsletter.d'
import {
    getGroupTranslation,
} from '../../../../common/utility/helpers'

interface EmailTemplate {
    id: number
    name: string
    category: string
}

interface NewsletterFormProps {
    newsletter?: Newsletter | null
    isEditing?: boolean
    translations?: Record<string, any>
}

function extractErrorMessage(data: any): string {
    if (!data) return 'Failed to save newsletter'
    const errors = data.errors
    if (!errors) return data.error || data.message || 'Failed to save newsletter'
    if (Array.isArray(errors)) return errors.join(', ')
    if (typeof errors === 'object') {
        return Object.values(errors)
            .flat()
            .join(', ')
    }
    return String(errors)
}

const NewsletterForm: React.FC<NewsletterFormProps> = ({
    newsletter,
    isEditing = false,
    translations: loadedTranslations,
}) => {
    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()
    const { triggerToast } = useToast()
    const initData = useAppSelector((state: any) => state.security.initData)
    const languagesData = initData?.languages?.data || {}

    const pageCode = isEditing && newsletter?.id
        ? 'NEWSLETTER_' + newsletter.id
        : 'TMPSET'

    const [templates, setTemplates] = useState<EmailTemplate[]>([])
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
        newsletter?.template_id ? String(newsletter.template_id) : ''
    )
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)

    const buildInitialFields = useCallback(() => {
        const f: Record<string, string> = {
            subject: pageCode + '_SUBJECT',
            body: pageCode + '_BODY',
        }

        Object.keys(languagesData).forEach((locale) => {
            const lang: Language = languagesData[locale]
            f[`subject_${lang.id}`] = ''
            f[`body_${lang.id}`] = ''
        })

        if (isEditing && newsletter && loadedTranslations) {
            Object.keys(languagesData).forEach((locale) => {
                const lang: Language = languagesData[locale]
                const groupTranslations = getGroupTranslation(
                    loadedTranslations as any,
                    [
                        { key: 'subject', code: newsletter.subject },
                        { key: 'body', code: newsletter.body },
                    ],
                    locale
                )
                f[`subject_${lang.id}`] = groupTranslations['subject'] || ''
                f[`body_${lang.id}`] = groupTranslations['body'] || ''
            })
        }

        return f
    }, [pageCode, languagesData, isEditing, newsletter, loadedTranslations])

    const [fields, setFields] = useState<Record<string, string>>(buildInitialFields)
    const [recipientFilter, setRecipientFilter] = useState(
        newsletter?.recipient_filter ?? 'active'
    )
    const [isSaving, setIsSaving] = useState(false)
    const [errMsg, setErrMsg] = useState('')

    useEffect(() => {
        if (isEditing && loadedTranslations) {
            setFields(buildInitialFields())
        }
    }, [isEditing, loadedTranslations, buildInitialFields])

    const loadTemplates = useCallback(async () => {
        setIsLoadingTemplates(true)
        try {
            const response = await axiosPrivate.get(
                '/newsletters/email-templates'
            )
            if (response.data?.success) {
                setTemplates(response.data.data.templates || [])
            }
        } catch {
            // Non-critical
        } finally {
            setIsLoadingTemplates(false)
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadTemplates()
    }, [loadTemplates])

    const handleTemplateChange = async (templateId: string) => {
        setSelectedTemplateId(templateId)
        if (!templateId) {
            const cleared = { ...fields }
            Object.keys(languagesData).forEach((locale) => {
                const lang: Language = languagesData[locale]
                cleared[`body_${lang.id}`] = ''
            })
            setFields(cleared)
            return
        }

        const template = templates.find(
            (t) => t.id === parseInt(templateId, 10)
        )
        if (template) {
            const updated = { ...fields }

            try {
                const res = await axiosPrivate.get(
                    `/newsletters/email-templates/${template.id}`
                )
                if (res.data?.success && res.data.data?.defaults) {
                    Object.entries(res.data.data.defaults).forEach(
                        ([key, value]) => {
                            updated[key] = value as string
                        }
                    )
                }
            } catch {
                // Ignore — fields remain empty for manual entry
            }

            setFields(updated)
        }
    }

    const updateField = (key: string, value: string) => {
        setFields((prev) => ({ ...prev, [key]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const hasAnySubject = Object.keys(languagesData).some((locale) => {
            const lang: Language = languagesData[locale]
            return fields[`subject_${lang.id}`]?.trim()
        })

        if (!hasAnySubject) {
            setErrMsg('Subject is required in at least one language.')
            return
        }

        const hasAnyBody = Object.keys(languagesData).some((locale) => {
            const lang: Language = languagesData[locale]
            return fields[`body_${lang.id}`]?.trim()
        })

        if (!hasAnyBody) {
            setErrMsg('Email body is required in at least one language.')
            return
        }

        setIsSaving(true)
        setErrMsg('')

        try {
            const url = isEditing
                ? `/newsletters/${newsletter?.id}`
                : '/newsletters/create'

            const payload: Record<string, unknown> = {
                ...fields,
                recipient_filter: recipientFilter,
            }

            if (selectedTemplateId) {
                payload.template_id = parseInt(selectedTemplateId, 10)
            }

            const response = await axiosPrivate.post(url, payload)

            if (response.data?.success) {
                triggerToast({
                    severity: 'success',
                    summary: 'Success!',
                    detail: response.data.data.msg,
                    life: 5000,
                })
                navigate('/people/clients/email-campaigns')
            } else {
                setErrMsg(extractErrorMessage(response.data))
            }
        } catch (err: any) {
            setErrMsg(extractErrorMessage(err?.response?.data))
        } finally {
            setIsSaving(false)
        }
    }

    const isReadOnly = newsletter?.status === 'sent'
    const usingTemplate = !!selectedTemplateId

    return (
        <Card>
            <Card.Body>
                {errMsg && <Alert variant="danger">{errMsg}</Alert>}
                {isReadOnly && (
                    <Alert variant="info">
                        This email campaign has already been sent and cannot be
                        edited.
                    </Alert>
                )}
                <Form onSubmit={handleSubmit}>
                    <fieldset>
                        <legend>Settings</legend>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Email Template{' '}
                                        <small className="text-muted">
                                            (optional)
                                        </small>
                                    </Form.Label>
                                    {isLoadingTemplates ? (
                                        <div className="py-2">
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                            />{' '}
                                            <small>Loading templates...</small>
                                        </div>
                                    ) : (
                                        <Form.Select
                                            value={selectedTemplateId}
                                            onChange={(e) =>
                                                handleTemplateChange(
                                                    e.target.value
                                                )
                                            }
                                            disabled={isReadOnly}
                                        >
                                            <option value="">
                                                — No template (custom content) —
                                            </option>
                                            {templates.map((tpl) => (
                                                <option
                                                    key={tpl.id}
                                                    value={tpl.id}
                                                >
                                                    {tpl.name}
                                                    {tpl.category
                                                        ? ` (${tpl.category})`
                                                        : ''}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}
                                    {usingTemplate && (
                                        <Form.Text className="text-muted">
                                            The template signature will be used. Edit the pre-populated content below.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Recipients</Form.Label>
                                    <Form.Select
                                        value={recipientFilter}
                                        onChange={(e) =>
                                            setRecipientFilter(e.target.value)
                                        }
                                        disabled={isReadOnly}
                                    >
                                        <option value="active">
                                            Active subscribers only
                                        </option>
                                        <option value="all">
                                            All subscribers
                                        </option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </fieldset>

                    <Tabs
                        id="newsletterLangTabs"
                        className="mb-0 mt-3"
                    >
                        {Object.keys(languagesData).map((locale: string) => {
                            const lang: Language = languagesData[locale]

                            return (
                                <Tab
                                    eventKey={locale}
                                    title={lang.name}
                                    key={locale}
                                    className="fade border-start border-end border-bottom mb-3 p-3 in-tab-content"
                                >
                                    <Form.Group className="mb-3">
                                        <Form.Label>Subject</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Email subject"
                                            value={
                                                fields[`subject_${lang.id}`] ||
                                                ''
                                            }
                                            onChange={(e) =>
                                                updateField(
                                                    `subject_${lang.id}`,
                                                    e.target.value
                                                )
                                            }
                                            disabled={isReadOnly}
                                        />
                                    </Form.Group>

                                    <Form.Group>
                                        <Form.Label>Email body content</Form.Label>
                                        <HtmlEditor
                                            key={`body_${lang.id}`}
                                            value={
                                                fields[
                                                    `body_${lang.id}`
                                                ] || ''
                                            }
                                            onChange={(val) =>
                                                updateField(
                                                    `body_${lang.id}`,
                                                    val
                                                )
                                            }
                                            disabled={isReadOnly}
                                            placeholder={`Write content in ${lang.name}...`}
                                        />
                                    </Form.Group>
                                </Tab>
                            )
                        })}
                    </Tabs>

                    {!isReadOnly && (
                        <div className="d-flex gap-2 justify-content-end mt-4">
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    navigate(
                                        '/people/clients/email-campaigns'
                                    )
                                }
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSaving}
                            >
                                {isSaving
                                    ? 'Saving...'
                                    : isEditing
                                    ? 'Update Email Campaign'
                                    : 'Create Email Campaign'}
                            </Button>
                        </div>
                    )}
                </Form>
            </Card.Body>
        </Card>
    )
}

export default NewsletterForm
